import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { find, get } from 'lodash';

import OrdersUtils from '../../utils/OrdersUtils';
import PartnerUtils from '../../utils/PartnerUtils';
import orderActions from '../../redux/actions/orders-actions';

import OrderDetailDialogActionsBox from './OrderDetailDialogActionsBox';

const mapStateToProps = (
  {
    orderDetailDialogReducer: { orderId },
    ordersReducer: { orders },
    partnerReducer: { profile },
  },
  { intl: { formatMessage } },
) => {
  const cancelButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.cancelButtonText',
  });
  const deliveredToRTText = formatMessage({
    id: 'orderDetailDialogActionsBox.deliveredToRTText',
  });
  const notDeliveredToRTText = formatMessage({
    id: 'orderDetailDialogActionsBox.notDeliveredToRTText',
  });
  const orderReadyButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.orderReadyButtonText',
  });
  const pendingToDeliverButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.pendingToDeliverButtonText.single',
  });
  const packageDeliveredButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.packageDeliverButtonText',
  });
  const sentToDeliverButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.sendToDeliveryButtonText',
  });
  const rejectButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.rejectButtonText',
  });
  const canceledMarketplaceOrderButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.canceledMarketplaceOrderButton',
  });
  const canceledPickupOrderButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.canceledPickupOrderButtonText',
  });

  const substitutionButtonText = formatMessage({
    id: 'orderDetailDialogActionsBox.substitutionButtonText',
  });

  const isPartnerIntegrated = PartnerUtils.isIntegratedPartner(profile);

  const orderRedux = find(orders, { id: +orderId }) ?? {
    id: +orderId,
    isBeingManaged: false,
  };
  const orderDetail = get(find(orders, { id: +orderId }), ['detail'], {
    id: +orderId,
    storekeeper: { id: 0 },
  });

  const isOrderEnabledToEdit = !!orderDetail?.is_enabled;

  const storeId =
    orderDetail.stores &&
    orderDetail.stores[0] &&
    orderDetail.stores[0].store_id;

  const isBeingManaged = OrdersUtils.isBeingManaged(orderRedux);

  const isInStore = OrdersUtils.isInStore(orderDetail.state);
  const isFullDelivery = OrdersUtils.isFullDelivery(
    orderDetail.delivery_method,
  );
  const isMarketPlace = OrdersUtils.isMarketPlace(orderDetail.delivery_method);
  const isPickup = OrdersUtils.isPickup(orderDetail.delivery_method);

  const isOrderCancel = OrdersUtils.isCanceled(orderDetail?.state ?? false);
  const isOrderReadyAndPack = OrdersUtils.isReadyAndPacked(
    orderDetail?.partner_order_state ?? null,
  );
  const isRTInStore = OrdersUtils.isRTInStoreFromDetail(orderDetail);

  const isOrderReady = isOrderReadyAndPack;
  const isReadyAndRTInStore = isInStore && isOrderReady && isRTInStore;
  const basicActionsInStore = isInStore && (!isFullDelivery || !isOrderReady);
  const needsActionInStore = basicActionsInStore || isReadyAndRTInStore;
  const showActionBox = isBeingManaged && (isOrderCancel || needsActionInStore);

  const showCookingOrderActions =
    isInStore && OrdersUtils.isBeingCooking(orderDetail.partner_order_state);

  const showNewOrderActions =
    !isPartnerIntegrated &&
    isInStore &&
    OrdersUtils.isNew(orderDetail.partner_order_state);

  const showPendingToDeliverOrderActions =
    isInStore && OrdersUtils.isReadyAndPacked(orderDetail.partner_order_state);

  const showCanceledOrderActions = OrdersUtils.isCanceled(orderDetail.state);
  const showCanceledFullDeliveryOrderActions =
    showCanceledOrderActions && isFullDelivery;
  const showCanceledMarketplaceOrderActions =
    showCanceledOrderActions && isMarketPlace;

  const showCanceledPickupOrderActions = showCanceledOrderActions && isPickup;

  const showSubstitutionFlowButtonAction =
    isInStore &&
    !orderDetail?.products.some(
      ({ replacement_suggestion }) => !!replacement_suggestion,
    );

  const cookingTime = orderDetail?.cooking_time ?? 0;
  const cookingTimeStartedAt = orderDetail?.cooking_time_started_at ?? 0;
  const isInCooking = OrdersUtils.isBeingCooking(
    orderDetail?.partner_order_state,
  );
  const {
    isOrderReadyButtonEnabled,
    buttonReadyPercentage,
  } = OrdersUtils.getOrderReadyButton({
    storeId,
    cookingTime,
    cookingTimeStartedAt,
    isInCooking,
    isMarketPlace,
  });

  const orderReadyButtonDisclaimerText = formatMessage(
    { id: 'orderDetailDialogActionsBox.orderReadyButtonDisclaimerText' },
    { percentage: buttonReadyPercentage },
  );

  const cookingLabelButton = isMarketPlace
    ? sentToDeliverButtonText
    : orderReadyButtonText;

  const pendingDeliveryLabelButton = isMarketPlace
    ? packageDeliveredButtonText
    : pendingToDeliverButtonText;

  const showCancelBtnToMP =
    isMarketPlace &&
    showCookingOrderActions &&
    !Boolean(orderDetail?.substitution_flow_state);

  return {
    cancelButtonText,
    canceledMarketplaceOrderButtonText,
    canceledPickupOrderButtonText,
    deliveredToRTText,
    notDeliveredToRTText,
    orderReadyButtonText,
    order: orderDetail,
    partnerId: profile.id,
    pendingToDeliverButtonText,
    showActionBox,
    isBeingManaged,
    showCanceledOrderActions,
    showCanceledFullDeliveryOrderActions,
    showCanceledMarketplaceOrderActions,
    showCanceledPickupOrderActions,
    showCookingOrderActions,
    showNewOrderActions,
    showPendingToDeliverOrderActions,
    storeId,
    storekeeperId: orderDetail.storekeeper && orderDetail.storekeeper.id,
    packageDeliveredButtonText,
    sentToDeliverButtonText,
    substitutionButtonText,
    showSubstitutionFlowButtonAction,
    rejectButtonText,
    isOrderEnabledToEdit,
    isOrderReadyButtonEnabled,
    orderReadyButtonDisclaimerText,
    cookingLabelButton,
    pendingDeliveryLabelButton,
    showCancelBtnToMP,
  };
};

const mapDispatchToProps = {
  onClickDeliveredToRT: orderActions.setDeliveredToRTBeforeCanceledFromDialog,
  onClickNotDeliveredToRT:
    orderActions.setNotDeliveredToRTBeforeCanceledFromDialog,
  onClickOrderReadyButton: orderActions.executeOrderIsReadyFromDialog,
  onClickDeliverButton: orderActions.deliverOrderFromDialog,
  onClickRejectButton: orderActions.openOrderRejectionDialog,
  onClickCanceledMarketplaceOrderButton:
    orderActions.acknowledgeCanceledOrderFromDialog,
  onClickCanceledPickupOrderButton:
    orderActions.acknowledgeCanceledOrderFromDialog,
  onClickSubstitutionFlowButton: orderActions.executeSubstitutionFlowDialog,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(OrderDetailDialogActionsBox),
);
